import { useState } from 'react';

import { getSelectionRect } from '@selectjs/core';

import { TPopperPosition } from './types/TPopperPosition';

export default function useIQTextSelection({ IQPopperEnabled }: { IQPopperEnabled: boolean }) {
  const [popperPosition, setPopperPosition] = useState<TPopperPosition | null>(null);
  const [selectedText, setSelectedText] = useState<string | null>(null);

  const hideAskIQPopper = () => {
    setPopperPosition((popper) => (popper ? null : popper));
    setSelectedText((text) => (text ? null : text));
  };

  const handleTextSelectionStart = (event: React.PointerEvent<HTMLTextAreaElement>) => {
    if (!IQPopperEnabled) {
      return;
    }

    const element = event.currentTarget;
    // Keep receiving pointer events even if mouse moves outside
    element.setPointerCapture(event.pointerId);
  };

  const handleTextSelectionEnd = (event: React.PointerEvent<HTMLTextAreaElement>) => {
    if (!IQPopperEnabled) {
      return;
    }

    const highlightedText = event.currentTarget;

    // Defer so the browser finalizes selection
    setTimeout(() => {
      const start = highlightedText.selectionStart ?? 0;
      const end = highlightedText.selectionEnd ?? 0;
      const hasHighlightedText = end > start;

      if (!hasHighlightedText) {
        hideAskIQPopper();
        highlightedText.releasePointerCapture(event.pointerId);
        return;
      }

      const selectedText = highlightedText.value.slice(start, end);
      setSelectedText(selectedText.trim());

      const geom = getSelectionRect(highlightedText);

      if (!geom?.rect) {
        highlightedText.releasePointerCapture(event.pointerId);
        return;
      }

      const { left, top, width, height } = geom.rect;
      setPopperPosition({ left, top, width, height });

      highlightedText.releasePointerCapture(event.pointerId);
    }, 0);
  };

  return {
    popperPosition,
    selectedText,
    handleTextSelectionStart,
    handleTextSelectionEnd,
    hideAskIQPopper,
  };
}
